import { useState, useEffect } from "react";
import { useLocation } from "wouter";
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { Header } from "@/components/Header";
import { ProgressIndicator } from "@/components/ProgressIndicator";
import { SmartAIChat } from "@/components/SmartAIChat";
import { SortableQuestionItem } from "@/components/SortableQuestionItem";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ArrowLeft, ArrowRight, Shuffle, ArrowUpDown, FolderTree, ShuffleIcon, Sparkles, ChevronUp, ChevronDown } from "lucide-react";
import { useTest } from "@/context/TestContext";
import { useToast } from "@/hooks/use-toast";
import { useLayoutState } from "@/hooks/use-layout-state";
import {
  sortQuestionsByCognitiveLevel,
  randomizeQuestions,
  sortQuestionsByTopic,
  randomizeWithinDifficulty,
  randomizeWithinTopic,
} from "@/utils/sortQuestions";

export default function FinalizeTest() {
  const [, setLocation] = useLocation();
  const { config, blueprint, questions, setQuestions, directions, setDirections } = useTest();
  const { toast } = useToast();
  const { layoutState } = useLayoutState();
  
  // Test Order state
  const [orderedQuestions, setOrderedQuestions] = useState(questions);
  
  // Test Directions state. We seed with a short editable placeholder so the
  // teacher has something to start from — most cases only need a tweak (e.g.
  // adding the time limit). The textarea is fully editable. The total-items
  // count is interpolated from the live questions array so the placeholder
  // always reflects the real test size.
  const DEFAULT_DIRECTIONS = `Read each question carefully and choose the best answer. This test has ${questions.length} total items. When you are finished, review your answers if time allows.`;
  const [localDirections, setLocalDirections] = useState(() => directions || DEFAULT_DIRECTIONS);

  // Mobile panel collapse state
  const [isMobilePanelCollapsed, setIsMobilePanelCollapsed] = useState(false);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  if (!blueprint || questions.length === 0) {
    setLocation("/create/review-test");
    return null;
  }


  // Sync ordered questions with context when questions change (e.g., from other pages)
  useEffect(() => {
    setOrderedQuestions(questions);
  }, [questions]);

  // Seed context with the default directions on first mount so downstream
  // pages (Share / PDF download) see something even if the user never
  // touches the textarea.
  useEffect(() => {
    if (!directions) setDirections(DEFAULT_DIRECTIONS);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDirectionsChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLocalDirections(e.target.value);
  };

  const handleSaveDirections = () => {
    setDirections(localDirections);
  };

  // Test Order handlers
  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setOrderedQuestions((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);
        
        const reordered = arrayMove(items, oldIndex, newIndex);
        setQuestions(reordered); // Persist to context immediately
        return reordered;
      });
    }
  };

  // Reorder helpers persist to context immediately (matches drag behavior).
  const applyOrder = (next: typeof orderedQuestions) => {
    setOrderedQuestions(next);
    setQuestions(next);
  };

  const handleRandomize = () => {
    applyOrder(randomizeQuestions(orderedQuestions));
    toast({
      title: "Randomized",
      description: "Questions have been randomized",
    });
  };

  const handleSortByDifficulty = () => {
    applyOrder(sortQuestionsByCognitiveLevel(orderedQuestions));
    toast({
      title: "Sorted by Difficulty",
      description: "Questions sorted from Knowledge to Application+",
    });
  };

  const handleSortByTopic = () => {
    applyOrder(sortQuestionsByTopic(orderedQuestions, blueprint));
    toast({
      title: "Sorted by Topic",
      description: "Questions grouped by topic",
    });
  };

  const handleRandomizeWithinDifficulty = () => {
    applyOrder(randomizeWithinDifficulty(orderedQuestions));
    toast({
      title: "Randomized within Difficulty",
      description: "Questions shuffled within each difficulty level",
    });
  };

  const handleRandomizeWithinTopic = () => {
    applyOrder(randomizeWithinTopic(orderedQuestions, blueprint));
    toast({
      title: "Randomized within Topic",
      description: "Questions shuffled within each topic",
    });
  };

  const getTopicName = (topicId: string) => {
    return blueprint?.topics.find((t) => t.id === topicId)?.name || "Unknown Topic";
  };

  const handleBack = () => {
    setLocation("/create/review-test");
  };

  const handleAccept = () => {
    // Save directions if changed
    if (localDirections !== directions) {
      setDirections(localDirections);
    }
    
    // Save ordered questions
    setQuestions(orderedQuestions);
    
    toast({
      title: "Test Finalized",
      description: "Your test is ready to share",
    });
    setLocation("/create/share");
  };

  const topicsCount = blueprint?.topics.length || 0;

  // Calculate toast position based on layout state
  const getToastPosition = () => {
    if (layoutState.isCondensed) {
      // On mobile/condensed layout
      if (isMobilePanelCollapsed) {
        return '20rem'; // Far above collapsed panel
      } else {
        return '36rem'; // Far above expanded panel
      }
    } else {
      // On desktop/wide layout
      return '1rem'; // Bottom right corner
    }
  };

  return (
    <div 
      className="min-h-screen bg-background" 
      style={{
        '--toast-bottom': getToastPosition()
      } as React.CSSProperties}
    >
      <Header pageTitle="Create Test" />

      <main className="container mx-auto px-6 py-12 max-w-7xl">
        <ProgressIndicator currentStep={6} />

        <div className="mb-8">
          <h1 className="text-3xl font-bold text-foreground mb-2">
            Finalize Test
          </h1>
          <p className="text-muted-foreground">
            Add directions and finalize question order before sharing your test
          </p>
        </div>

        {/* Combined Finalize content */}
        <div className="space-y-6">
          {/* Main Content Area */}
          <div className="lg:grid lg:grid-cols-[1fr_320px] lg:gap-6">
            <div className={`space-y-8 lg:pb-0 ${isMobilePanelCollapsed ? 'pb-24' : 'pb-80'}`}>
              {/* Test Directions */}
              <Card>
                <CardHeader className="pb-4">
                  <CardTitle>Test Directions</CardTitle>
                </CardHeader>
                <CardContent>
                  <Textarea
                    value={localDirections}
                    onChange={handleDirectionsChange}
                    onBlur={handleSaveDirections}
                    className="min-h-[200px] text-sm"
                    placeholder="Enter test directions here..."
                    data-testid="textarea-directions"
                  />
                  <p className="text-sm text-muted-foreground mt-2">
                    These directions will appear at the beginning of the test. Edit as needed.
                  </p>
                </CardContent>
              </Card>

              {/* Test Order */}
              <div>
                <h2 className="text-xl font-semibold text-foreground mb-4">Test Order</h2>
                <DndContext
                  sensors={sensors}
                  collisionDetection={closestCenter}
                  onDragEnd={handleDragEnd}
                >
                  <SortableContext
                    items={orderedQuestions.map((q) => q.id)}
                    strategy={verticalListSortingStrategy}
                  >
                    <div className="space-y-1">
                      {orderedQuestions.map((question, index) => (
                        <SortableQuestionItem
                          key={question.id}
                          question={question}
                          index={index}
                          topicName={getTopicName(question.topicId)}
                        />
                      ))}
                    </div>
                  </SortableContext>
                </DndContext>
              </div>

              {/* Mobile-only nav. Desktop has the same pair in the sidebar
                  Ordering Options card. */}
              <div className="lg:hidden flex justify-between mt-8">
                <Button variant="outline" onClick={handleBack} data-testid="button-back-mobile">
                  <ArrowLeft className="w-4 h-4 mr-2" />
                  Back to Review
                </Button>
                <Button onClick={handleAccept} data-testid="button-share-mobile">
                  Share Test
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </div>
            </div>

            {/* Desktop Sidebar - Hidden on mobile.
                  space-y-6 is on the grid item itself (no inner wrapper)
                  so the sticky context is the full grid-row height — both
                  chat and card follow the user's scroll throughout the
                  page, not just the first ~840px.

                  Viewport-aware sticky:
                    • Tall (≥900px): chat is sticky top-6, card is sticky
                      top-[504px] (just below chat with a 24px gap). Both
                      pin to the top of the viewport and follow scroll.
                    • Short (<900px): chat is in normal flow (scrolls off
                      with the page). Card is sticky top-6 — initially
                      below the fold, scrolls up into view, then pins to
                      the top of the viewport so it stays visible while
                      the user scrolls further down.
                  Extend chevron is hidden via isCollapsible={false} —
                  extending here only produced overlap with the card. */}
            <div className="hidden lg:block space-y-6">
              <div className="h-[calc(480px-1.5rem)] [@media(min-height:900px)]:sticky [@media(min-height:900px)]:top-6">
                <SmartAIChat
                  context="general"
                  showWidthToggle={false}
                  isCollapsible={false}
                  className="h-full"
                />
              </div>

              <Card className="sticky top-6 [@media(min-height:900px)]:top-[calc(480px+1.5rem)]">
                  <CardContent className="pt-6">
                    <h3 className="font-semibold mb-4">Ordering Options</h3>
                    {/* 2-up grid keeps the pane short. Randomize spans the top
                        row; the two "Sort by" and two "Randomize within"
                        options pair up beneath it. */}
                    <div className="grid grid-cols-2 gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="col-span-2 w-full justify-start text-left whitespace-normal leading-tight h-auto py-2"
                        onClick={handleRandomize}
                        data-testid="button-randomize"
                      >
                        Randomize <Shuffle className="w-4 h-4 shrink-0" />
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start text-left whitespace-normal leading-tight h-auto py-2"
                        onClick={handleSortByDifficulty}
                        data-testid="button-sort-difficulty"
                      >
                        Sort by Difficulty <ArrowUpDown className="w-4 h-4 shrink-0" />
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start text-left whitespace-normal leading-tight h-auto py-2"
                        onClick={handleSortByTopic}
                        data-testid="button-sort-topic"
                      >
                        Sort by Topic <FolderTree className="w-4 h-4 shrink-0" />
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start text-left whitespace-normal leading-tight h-auto py-2"
                        onClick={handleRandomizeWithinDifficulty}
                        data-testid="button-randomize-difficulty"
                      >
                        Randomize within Difficulty <ShuffleIcon className="w-4 h-4 shrink-0" />
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start text-left whitespace-normal leading-tight h-auto py-2"
                        onClick={handleRandomizeWithinTopic}
                        data-testid="button-randomize-topic"
                      >
                        Randomize within Topic <ShuffleIcon className="w-4 h-4 shrink-0" />
                      </Button>
                    </div>

                    {/* Workflow navigation — pinned beneath the ordering
                        options so they're always reachable on the sidebar
                        regardless of question-list scroll position. */}
                    <div className="mt-6 pt-6 border-t border-border space-y-2">
                      <Button
                        className="w-full"
                        onClick={handleAccept}
                        data-testid="button-share"
                      >
                        Share Test
                        <ArrowRight className="w-4 h-4 ml-2" />
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full"
                        onClick={handleBack}
                        data-testid="button-back"
                      >
                        <ArrowLeft className="w-4 h-4 mr-2" />
                        Back to Review
                      </Button>
                    </div>
                </CardContent>
              </Card>
            </div>
          </div>

          {/* Mobile/Tablet Bottom Fixed Panel */}
          <div className="lg:hidden fixed bottom-0 left-0 right-0 bg-background border-t shadow-lg z-50">
            {isMobilePanelCollapsed ? (
              /* Collapsed State - Header bar on left, Options on right */
              <div className="flex min-h-16 h-auto max-h-24">
                {/* Collapsed AI Assistant Header */}
                <div
                  className="flex-none flex items-center"
                  onClick={() => setIsMobilePanelCollapsed(false)}
                >
                  <div className="flex items-center justify-between p-4 bg-secondary/10 cursor-pointer hover-elevate min-h-16 border-r w-full">
                    <div className="flex items-center gap-2">
                      <Sparkles className="w-5 h-5 text-secondary" />
                      <h3 className="font-semibold text-foreground">AI Assistant</h3>
                    </div>
                    <Button size="icon" variant="ghost" className="h-8 w-8">
                      <ChevronUp className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Options Panel - Takes remaining space when AI is collapsed */}
                <div className="flex-1 flex items-center p-2 overflow-y-auto">
                  <div className="flex flex-wrap items-center gap-1">
                    <h3 className="font-semibold text-foreground mr-2">Options</h3>
                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs px-2 py-1 h-auto"
                        onClick={handleRandomize}
                        data-testid="button-randomize-mobile-collapsed"
                      >
                        Randomize <Shuffle className="w-3 h-3" />
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs px-2 py-1 h-auto"
                        onClick={handleSortByDifficulty}
                        data-testid="button-sort-difficulty-mobile-collapsed"
                      >
                        By Difficulty <ArrowUpDown className="w-3 h-3" />
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs px-2 py-1 h-auto"
                        onClick={handleSortByTopic}
                        data-testid="button-sort-topic-mobile-collapsed"
                      >
                        By Topic <FolderTree className="w-3 h-3" />
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs px-2 py-1 h-auto"
                        onClick={handleRandomizeWithinDifficulty}
                        data-testid="button-randomize-difficulty-mobile-collapsed"
                      >
                        Rand. Difficulty <ShuffleIcon className="w-3 h-3" />
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        className="text-xs px-2 py-1 h-auto"
                        onClick={handleRandomizeWithinTopic}
                        data-testid="button-randomize-topic-mobile-collapsed"
                      >
                        Rand. Topic <ShuffleIcon className="w-3 h-3" />
                      </Button>
                    </div>
                  </div>
              </div>
            ) : (
              /* Expanded State - Full panel */
              <div className="flex h-80">
                {/* AI Assistant Panel - 75% */}
                <div className="w-3/4 border-r">
                  <div className="h-full">
                    <SmartAIChat
                      context="general"
                      className="h-full"
                      isCollapsible={true}
                      defaultCollapsed={false}
                      onCollapseChange={setIsMobilePanelCollapsed}
                      showWidthToggle={false}
                    />
                  </div>
                </div>

                {/* Options Panel - 25% */}
                <div className="w-1/4 p-4 overflow-y-auto">
                    <h3 className="font-semibold mb-3 text-sm">Options</h3>
                    <div className="space-y-2">
                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start text-xs"
                        onClick={handleRandomize}
                        data-testid="button-randomize-mobile"
                      >
                        Randomize <Shuffle className="w-3 h-3" />
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start text-xs"
                        onClick={handleSortByDifficulty}
                        data-testid="button-sort-difficulty-mobile"
                      >
                        By Difficulty <ArrowUpDown className="w-3 h-3" />
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start text-xs"
                        onClick={handleSortByTopic}
                        data-testid="button-sort-topic-mobile"
                      >
                        By Topic <FolderTree className="w-3 h-3" />
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start text-xs"
                        onClick={handleRandomizeWithinDifficulty}
                        data-testid="button-randomize-difficulty-mobile"
                      >
                        Rand. Difficulty <ShuffleIcon className="w-3 h-3" />
                      </Button>

                      <Button
                        variant="outline"
                        size="sm"
                        className="w-full justify-start text-xs"
                        onClick={handleRandomizeWithinTopic}
                        data-testid="button-randomize-topic-mobile"
                      >
                        Rand. Topic <ShuffleIcon className="w-3 h-3" />
                      </Button>
                    </div>

                    <div className="mt-3 pt-3 border-t">
                      <h4 className="font-semibold mb-2 text-xs">Summary</h4>
                      <div className="space-y-1 text-xs">
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Questions</span>
                          <span className="font-semibold">{orderedQuestions.length}</span>
                        </div>
                        <div className="flex justify-between">
                          <span className="text-muted-foreground">Topics</span>
                          <span className="font-semibold">{topicsCount}</span>
                        </div>
                      </div>
                    </div>
                  </div>
              </div>
            )}
          </div>
        </div>
      </main>
    </div>
  );
}
